<script lang="ts">
  import { gallery, type GalleryItem, type Kind, type Page } from '../gallery/api';
  import { t } from '../i18n/index.svelte';
  import { studio } from '../state.svelte';
  import { ui } from '../ui.svelte';

  let kind = $state<Kind | 'all'>('all');
  let page = $state(1);
  let data = $state<Page | null>(null);
  let error = $state('');
  let open = $state<GalleryItem | null>(null);
  let hovered = $state<string | null>(null);

  $effect(() => {
    const k = kind;
    const p = page;
    error = '';
    gallery
      .list(p, k)
      .then((d) => (data = d))
      .catch((e) => (error = e instanceof Error ? e.message : String(e)));
  });

  async function show(item: GalleryItem) {
    open = item;
    if (item.has_params) {
      try {
        open = await gallery.item(item.id);
      } catch {
        /* keep the list entry */
      }
    }
  }

  /** Applies shared stereogram settings to the studio. */
  function useSettings(p: Record<string, unknown>) {
    if (p.stereo) studio.stereo = { ...studio.stereo, ...(p.stereo as object) };
    if (p.texture) studio.texture = p.texture as typeof studio.texture;
    if (p.depth) studio.depth = { ...studio.depth, ...(p.depth as object) };
    open = null;
    ui.view = 'studio';
  }

  const isVideo = (i: GalleryItem) => i.mime.startsWith('video/');
</script>

<div class="gallery">
  <div class="bar row">
    <div class="seg">
      {#each ['all', 'static', 'animation'] as const as k (k)}
        <button class:on={kind === k} onclick={() => ((kind = k), (page = 1))} data-testid={`gallery-${k}`}
          >{t(`gallery.kind.${k}`)}</button
        >
      {/each}
    </div>
    {#if data}<span class="muted small">{t('gallery.count', { n: data.total })}</span>{/if}
  </div>

  {#if error}
    <p class="error pad">{error}</p>
  {:else if data && !data.items.length}
    <p class="muted pad" data-testid="gallery-empty">{t('gallery.empty')}</p>
  {:else if data}
    <div class="grid" data-testid="gallery-grid">
      {#each data.items as item (item.id)}
        <button
          class="card"
          onclick={() => show(item)}
          onmouseenter={() => (hovered = item.id)}
          onmouseleave={() => (hovered = null)}
          data-testid="gallery-item"
        >
          {#if hovered === item.id && item.kind === 'animation'}
            {#if isVideo(item)}
              <!-- svelte-ignore a11y_media_has_caption -->
              <video src={item.file_url} autoplay loop muted playsinline></video>
            {:else}
              <img src={item.file_url} alt={item.title} />
            {/if}
          {:else}
            <img src={item.preview_url} alt={item.title} loading="lazy" />
          {/if}
          {#if item.kind === 'animation'}<span class="badge">▶</span>{/if}
          <span class="title">{item.title || t('gallery.untitled')}</span>
        </button>
      {/each}
    </div>
    {#if data.pages > 1}
      <div class="row pager">
        <button disabled={page <= 1} onclick={() => page--}>←</button>
        <span class="mono small">{page} / {data.pages}</span>
        <button disabled={page >= data.pages} onclick={() => page++}>→</button>
      </div>
    {/if}
  {/if}
</div>

{#if open}
  <div class="backdrop" role="presentation" onclick={(e) => e.target === e.currentTarget && (open = null)}>
    <div class="viewer" role="dialog" aria-modal="true" data-testid="gallery-viewer">
      {#if isVideo(open)}
        <!-- svelte-ignore a11y_media_has_caption -->
        <video src={open.file_url} autoplay loop muted playsinline controls></video>
      {:else}
        <img src={open.file_url} alt={open.title} />
      {/if}
      <div class="meta">
        <div>
          <strong>{open.title || t('gallery.untitled')}</strong>
          {#if open.caption}<p class="muted">{open.caption}</p>{/if}
        </div>
        <div class="row">
          {#if open.params}
            <button onclick={() => useSettings(open!.params!)}>{t('gallery.useSettings')}</button>
          {/if}
          <a class="btn" href={open.file_url} download>{t('server.download')}</a>
          <button onclick={() => (open = null)}>{t('common.close')}</button>
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  .gallery {
    height: 100%;
    overflow-y: auto;
    padding: 16px 20px;
  }
  .bar {
    margin-bottom: 16px;
    gap: 16px;
  }
  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 14px;
  }
  .card {
    position: relative;
    padding: 0;
    border-radius: 10px;
    overflow: hidden;
    aspect-ratio: 16 / 10;
    background: #000;
  }
  .card img,
  .card video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .badge {
    position: absolute;
    top: 8px;
    right: 8px;
    background: #000a;
    border-radius: 99px;
    padding: 2px 8px;
    font-size: 11px;
  }
  .title {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 18px 10px 8px;
    background: linear-gradient(transparent, #000c);
    text-align: left;
    font-size: 13px;
  }
  .pager {
    justify-content: center;
    margin-top: 16px;
  }
  .pad {
    padding: 24px 0;
  }
  .small {
    font-size: 12px;
  }
  .backdrop {
    position: fixed;
    inset: 0;
    background: #000d;
    display: grid;
    place-items: center;
    z-index: 40;
    padding: 20px;
  }
  .viewer {
    max-width: min(1400px, 100%);
    max-height: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .viewer img,
  .viewer video {
    max-width: 100%;
    max-height: 80vh;
    object-fit: contain;
  }
  .meta {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    align-items: flex-start;
  }
  .meta p {
    margin: 4px 0 0;
    font-size: 13px;
  }
  .btn {
    border: 1px solid var(--line);
    background: var(--panel-2);
    border-radius: 6px;
    padding: 6px 12px;
    color: var(--text);
    text-decoration: none;
  }
</style>
